import { useState } from "react";
import { ApolloError } from "@apollo/client";
import { captureException } from "@sentry/browser";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { PDF_MAPPING_TYPE } from "common/constants";
import { getPath } from "common/routing";
import {
  AccountDocumentTemplate,
  CreateAccountDocumentTemplateMutationFn,
  UpdateAccountDocumentTemplateMutationFn,
  useGetAccountDocGenOptionsQuery,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { Button } from "../../../Common/Buttons/Button";
import FullPageFormLayout from "../../../Common/FullPageFormLayout";
import { Body, Title } from "../../../Common/Typography";
import { Label } from "../../../Inputs";
import {
  ButtonContainer,
  Container,
  FieldRow,
  FieldTable,
  Header,
  TableHeader,
} from "./__styles__/DocumentTemplates";
import { CreateDocumentTemplateForm } from "./CreateOrUpdateForm";
import MappingFieldRow from "./MappingFieldRow";

export interface MappingFormProps {
  subtitle: string;
  backButtonText: string;
  actionButtonText: string;
  loadingAction: boolean;
  canDoAction: boolean;
  documentTemplate?: Pick<
    AccountDocumentTemplate,
    "id" | "fieldMappings" | "customMapId" | "submissionTypeId"
  >;
  type: "update" | "create";
  onBack: () => void;
  onSubmit:
    | CreateAccountDocumentTemplateMutationFn
    | UpdateAccountDocumentTemplateMutationFn;
}

const MappingForm = ({
  subtitle,
  backButtonText,
  actionButtonText,
  loadingAction,
  canDoAction,
  documentTemplate,
  type,
  onBack,
  onSubmit,
}: MappingFormProps) => {
  const {
    control,
    getValues,
    handleSubmit,
    resetField,
    setValue,
    formState: { errors, isValid },
  } = useFormContext<CreateDocumentTemplateForm>();
  const location = useLocation<undefined | { prevLocation?: string }>();

  const customMapId = getValues("customMapId") ?? documentTemplate?.customMapId;
  const submissionTypeId =
    getValues("submissionTypeId") ?? documentTemplate?.submissionTypeId;
  const { data, loading } = useGetAccountDocGenOptionsQuery({
    variables: { customMapId, submissionTypeId },
  });

  const docGenOptions = !loading ? (data?.account?.docGenOptions ?? []) : [];
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const [hasUpdatedTemplateSuccessfully, setHasCreatedTemplateSuccessfully] =
    useState(false);

  const onError = (error: Error) => {
    captureException(error);
    addErrorToast(
      "There was a problem updating your template. Please try again or contact support dev",
    );
  };

  const submit = async (data: CreateDocumentTemplateForm) => {
    const onSubmitParams = {
      onCompleted: () => {
        const verb = type === "update" ? "updated" : "saved";
        addSuccessToast(`Your template was successfully ${verb}`);
        setHasCreatedTemplateSuccessfully(true);
        setTimeout(() => {
          const currentUrl = window.location.href;
          const newUrl = currentUrl.replace(/\/[^/]+$/, "");
          window.location.href = newUrl;
        }, 1500);
      },
      onError: (error: ApolloError) => onError(error),
    };

    if (type === "update") {
      await (onSubmit as UpdateAccountDocumentTemplateMutationFn)({
        variables: {
          id: documentTemplate!.id,
          fieldMappings: data.mapping,
        },
        ...onSubmitParams,
      });
    } else {
      await (onSubmit as CreateAccountDocumentTemplateMutationFn)({
        variables: {
          data: {
            name: data.name,
            filename: data.s3Key,
            fieldMappings: data.mapping,
            accountDocumentTypeId: data.associatedDocumentTypeId,
            customMapId: data.customMapId,
            submissionTypeId: data.submissionTypeId,
            documentUploadFormDataPath: data.documentUploadFormDataPath,
          },
        },
        ...onSubmitParams,
      });
    }
  };

  const rightContainer = (
    <ButtonContainer>
      <Button
        variant="secondary"
        size="medium"
        onPress={onBack}
        title={backButtonText}
      />
      <Button
        variant="primary"
        size="medium"
        isDisabled={!canDoAction || hasUpdatedTemplateSuccessfully || !isValid}
        isPending={loadingAction}
        onPress={() => handleSubmit(submit)()}
        title={actionButtonText}
      />
    </ButtonContainer>
  );

  const prevLocation =
    location.state?.prevLocation ?? getPath("documentTemplates");

  const mappingOptions = [
    ...docGenOptions,
    // Because blank needs to be last, appending it manually to the end of the list
    {
      label: "(empty)",
      value: "blank",
      type: PDF_MAPPING_TYPE.ANY,
    },
  ];

  return (
    <FullPageFormLayout
      centered={true}
      overflows={true}
      width={"wide"}
      subtitle={subtitle}
      prevLocation={prevLocation}
      rightContainer={rightContainer}
    >
      <Container>
        <Header>
          <Title size="small" type="regular">
            Field mapping
          </Title>
          <Body size="small" type="regular">
            Please map the smart fields in your PDF file to the fields in
            Forerunner. For more information on document template field mapping,
            please see our help article.
          </Body>
        </Header>
        <FieldTable>
          <TableHeader>
            <FieldRow>
              <th>PDF smart field name</th>
              <th>
                <Label
                  required
                  text="Forerunner field name"
                  style={{ marginBottom: "0" }}
                />
              </th>
            </FieldRow>
          </TableHeader>
          <tbody>
            {getValues("mapping").map((f, index) => (
              <MappingFieldRow
                key={f.label}
                index={index}
                field={f}
                mappingOptions={mappingOptions}
                control={control}
                getValues={getValues}
                setValue={setValue}
                resetField={resetField}
                errors={errors}
                loading={loading}
              />
            ))}
          </tbody>
        </FieldTable>
      </Container>
    </FullPageFormLayout>
  );
};

export default MappingForm;
